'use client'
import { Accordion, Box, Flex, Skeleton, Stack, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { Suspense } from 'react'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import { SectionErrorBoundary } from '@/components/error/boundary'

import QuestionItem from './QuestionItem'
import styles from './style.module.css'
// Define FAQ item type
type FAQItemRaw = { question: string; answer: string }
type FAQItemRendered = { question: string; answer: React.ReactNode }

function FaqListFallback() {
  return (
    <Box
      mt={80}
      style={{
        borderRadius: 16,
        maxWidth: '100%',
      }}
    >
      {/* Title skeleton */}
      <Skeleton
        h={32}
        w={160}
        mb={12}
        style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
      />

      {/* FAQ items skeleton */}
      <Stack gap={0}>
        {[0, 1, 2, 3].map(index => (
          <Flex
            key={index}
            justify="space-between"
            align="center"
            className={styles.faqFallbackItem}
            w="100%"
            h={52}
          >
            <Skeleton
              h={20}
              w={320}
              style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
            />

            {/* Circular expand/collapse indicator skeleton */}
            <Skeleton
              h={20}
              w={20}
              style={{
                backgroundColor: 'var(--bg-surface-secondary)',
                borderRadius: '50%',
                flexShrink: 0,
              }}
            />
          </Flex>
        ))}
      </Stack>
    </Box>
  )
}

function FaqListContent() {
  const { coin } = useCoinContext()
  const t = useTranslations()

  // Get FAQ data based on coin symbol with fallback
  const getFaqData = (): FAQItemRendered[] | null => {
    if (!coin?.symbol) return null
    const symbol = coin.symbol.toUpperCase()
    const translationKey = `faq.${symbol}`

    // Check if the translation key exists first
    if (!t.has(translationKey)) {
      return null
    }

    // Get the raw translation data
    const raw = t.raw(translationKey)

    // Check if the data is an array of FAQ items
    if (Array.isArray(raw) && raw.length > 0 && raw[0].question) {
      return (raw as FAQItemRaw[]).map((item, index) => {
        const answerKey = `${translationKey}.${index}.answer`

        return {
          question: item.question,
          answer: t.rich(answerKey, {
            br: () => <br />,
            link: chunks => {
              const url = String(chunks)
              return (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--common-primary)' }}
                >
                  {url}
                </a>
              )
            },
            indentation: chunks => <div>{chunks}</div>,
            indentationAndMargin: chunks => <div>{chunks}</div>,
          }),
        }
      })
    }

    return null
  }

  const faqData = getFaqData()

  if (!faqData || faqData.length === 0) return null

  return (
    <Box
      mt={80}
      style={{
        borderRadius: 16,
        maxWidth: '100%',
      }}
    >
      <Text variant={'heading2'} mb={12} c="var(--text-primary)">
        {t('faqTitle', { coinName: coin.name })}
      </Text>

      <Accordion
        variant="unstyled"
        styles={{
          content: {
            padding: 0,
          },
          panel: {
            padding: 0,
          },
        }}
      >
        {faqData.map((faq: FAQItemRendered, index: number) => (
          <QuestionItem
            key={index}
            value={`faq-${index}`}
            question={faq.question}
            answer={faq.answer}
            isLast={index === faqData.length - 1}
          />
        ))}
      </Accordion>
    </Box>
  )
}

function FaqList() {
  return (
    <Suspense fallback={<FaqListFallback />}>
      <FaqListContent />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(FaqList)
